import {
    ArchiveBookmarksDocument,
    MixArchiveBookmarkedIdsDocument,
    useDeleteArchiveBookmarkMutation,
    useInsertArchiveBookmarkMutation
} from "graphql/generated";
import { fireEvent } from "lib/ga";
import { useCallback } from "react";

import { useAuth } from "./useAuth";

type MixArchiveBookmarkedIdsQuery = {
    mix_archive_bookmark: Array<{ __typename?: string; archiveid: string }>;
};

type ArchiveBookmarksQuery = {
    mix_archive_bookmark: Array<{
        __typename?: string;
        id: string;
        archiveid: string;
        updatetime?: any;
        mix_archive: any; // Full archive object
    }>;
};

export const useArchiveBookmark = (archive: {
    id: string;
    title: string;
    view_url: string;
    bookmarked?: boolean;
}) => {
    const { isLoggedIn, openSignInModal, me } = useAuth();

    const [insertBookmark] = useInsertArchiveBookmarkMutation({
        optimisticResponse: {
            insert_mix_archive_bookmark_one: {
                __typename: "mix_archive_bookmark",
                id: archive.id,
                archiveid: archive.id,
                userid: me?.id || ""
            }
        },
        update: (cache, { data }) => {
            if (data?.insert_mix_archive_bookmark_one && me?.id) {
                // Update the bookmarked IDs query cache
                const existingData =
                    cache.readQuery<MixArchiveBookmarkedIdsQuery>({
                        query: MixArchiveBookmarkedIdsDocument,
                        variables: { userId: me.id }
                    });

                if (existingData) {
                    cache.writeQuery<MixArchiveBookmarkedIdsQuery>({
                        query: MixArchiveBookmarkedIdsDocument,
                        variables: { userId: me.id },
                        data: {
                            mix_archive_bookmark: [
                                ...existingData.mix_archive_bookmark,
                                {
                                    __typename: "mix_archive_bookmark",
                                    archiveid: archive.id
                                }
                            ]
                        }
                    });
                }
            }
        },
        onCompleted: () => {
            fireEvent({
                event: "archive_bookmark",
                params: {
                    archive_id: archive.id,
                    archive_title: archive.title,
                    archive_url: archive.view_url,
                    value: true
                }
            });
        }
    });

    const [deleteBookmark] = useDeleteArchiveBookmarkMutation({
        optimisticResponse: {
            delete_mix_archive_bookmark: {
                __typename: "mix_archive_bookmark_mutation_response",
                affected_rows: 1
            }
        },
        update: (cache, { data }) => {
            if (data?.delete_mix_archive_bookmark && me?.id) {
                // Update the bookmarked IDs query cache (used by archive & search pages)
                const existingIdsData =
                    cache.readQuery<MixArchiveBookmarkedIdsQuery>({
                        query: MixArchiveBookmarkedIdsDocument,
                        variables: { userId: me.id }
                    });

                if (existingIdsData) {
                    cache.writeQuery<MixArchiveBookmarkedIdsQuery>({
                        query: MixArchiveBookmarkedIdsDocument,
                        variables: { userId: me.id },
                        data: {
                            mix_archive_bookmark:
                                existingIdsData.mix_archive_bookmark.filter(
                                    (bookmark: {
                                        __typename?: string;
                                        archiveid: string;
                                    }) => bookmark.archiveid !== archive.id
                                )
                        }
                    });
                }

                // Note: We don't remove items from ArchiveBookmarksQuery cache
                // This keeps the card visible on the bookmark page, just showing as un-bookmarked
                // The bookmark page will check bookmarkedArchiveIds to determine actual bookmark status
            }
        },
        onCompleted: () => {
            fireEvent({
                event: "archive_bookmark",
                params: {
                    archive_id: archive.id,
                    archive_title: archive.title,
                    archive_url: archive.view_url,
                    value: false
                }
            });
        }
    });

    const toggle = useCallback(() => {
        if (isLoggedIn) {
            if (!archive.bookmarked) {
                insertBookmark({
                    variables: {
                        archiveId: archive.id
                    }
                });
            } else {
                deleteBookmark({
                    variables: {
                        archiveId: archive.id
                    }
                });
            }
        } else {
            openSignInModal();
        }
    }, [
        archive.id,
        archive.bookmarked,
        isLoggedIn,
        insertBookmark,
        deleteBookmark,
        openSignInModal
    ]);

    return {
        toggle
    };
};
